<template>
    <div class="ui-file-drop">
        <!-- Arquivo novo escolhido, ainda nao salvo -->
        <div v-if="file" class="ui-file-drop__item ui-file-drop__item--pending">
            <div class="ui-file-drop__preview" :class="{ 'ui-file-drop__preview--empty': !previewKind }">
                <img v-if="previewKind === 'image'" :src="fileUrl" :alt="file.name" />
                <iframe v-else-if="previewKind === 'pdf'" :src="fileUrl" title="Pré-visualização do arquivo"></iframe>
                <v-icon v-else size="36">mdi-file-outline</v-icon>
            </div>
            <div class="ui-file-drop__meta">
                <div class="ui-file-drop__name" :title="file.name">{{ file.name }}</div>
                <div class="ui-file-drop__info">{{ formatSize(file.size) }} · ainda não salvo</div>
            </div>
            <div class="ui-file-drop__actions">
                <v-btn small outlined :disabled="loading" @click="$emit('select', null)">Cancelar</v-btn>
                <v-btn v-if="$allowed(saveAction)" small color="primary" depressed :loading="loading" @click="$emit('save')">{{ currentUrl ? "Substituir" : "Salvar" }}</v-btn>
            </div>
        </div>

        <!-- Documento atual -->
        <div v-else-if="currentUrl" class="ui-file-drop__item">
            <a class="ui-file-drop__preview" :class="{ 'ui-file-drop__preview--empty': !currentKind }" :href="currentUrl" target="_blank" rel="noopener" title="Abrir em nova guia">
                <img v-if="currentKind === 'image'" :src="currentUrl" :alt="currentName" />
                <iframe v-else-if="currentKind === 'pdf'" :src="currentUrl" title="Documento atual"></iframe>
                <v-icon v-else size="36">mdi-file-outline</v-icon>
            </a>
            <div class="ui-file-drop__meta">
                <div class="ui-file-drop__name" :title="currentName">{{ currentName }}</div>
                <div class="ui-file-drop__info">Documento atual</div>
            </div>
            <div class="ui-file-drop__actions">
                <v-btn small outlined :href="currentUrl" target="_blank" rel="noopener"><v-icon left small>mdi-open-in-new</v-icon>Abrir</v-btn>
                <v-btn small outlined :disabled="loading" @click="pick"><v-icon left small>mdi-swap-horizontal</v-icon>Substituir</v-btn>
            </div>
        </div>

        <!-- Sem documento: area de soltar -->
        <button v-else type="button" class="ui-file-drop__zone" :class="{ 'ui-file-drop__zone--over': dragging }" :disabled="loading"
            @click="pick" @dragover.prevent="dragging = true" @dragleave.prevent="dragging = false" @drop.prevent="onDrop">
            <v-icon size="28">mdi-tray-arrow-up</v-icon>
            <span class="ui-file-drop__zone-title">Arraste um arquivo ou clique para escolher</span>
            <span class="ui-file-drop__zone-hint">{{ hint }}</span>
        </button>

        <input ref="input" type="file" class="d-none" :accept="accept" @change="onInput" />
    </div>
</template>

<script>
// Campo de arquivo com area de soltar, pre-visualizacao (imagem e PDF) e os
// estados "sem arquivo", "arquivo atual" e "arquivo novo a salvar".
// Eventos: select(file | null) e save. O envio fica com a tela.
export default {
    name: "UiFileDrop",
    props: {
        // Permissao do botao salvar (perfil de acesso): "save" ou uma acao especifica.
        saveAction: { type: String, default: "save" },
        // Arquivo escolhido e ainda nao salvo.
        file: { type: [File, Object], default: null },
        // URL do arquivo ja salvo.
        currentUrl: { type: String, default: null },
        accept: { type: String, default: "image/png,image/jpeg,application/pdf" },
        maxSizeMb: { type: Number, default: 15 },
        hint: { type: String, default: "PNG, JPG ou PDF de até 15 MB" },
        loading: { type: Boolean, default: false },
    },
    data: () => ({ dragging: false, fileUrl: null }),
    computed: {
        previewKind() {
            return this.file ? this.kindOf(this.file.type, this.file.name) : null;
        },
        currentKind() {
            return this.currentUrl ? this.kindOf(null, this.currentUrl) : null;
        },
        currentName() {
            if (!this.currentUrl) return "";
            const path = this.currentUrl.split("?")[0];
            return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1)) || "Documento";
        },
    },
    watch: {
        file: {
            immediate: true,
            handler(file) {
                if (this.fileUrl) URL.revokeObjectURL(this.fileUrl);
                this.fileUrl = file instanceof Blob ? URL.createObjectURL(file) : null;
            },
        },
    },
    beforeDestroy() {
        if (this.fileUrl) URL.revokeObjectURL(this.fileUrl);
    },
    methods: {
        kindOf(type, name) {
            const value = `${type || ""} ${(name || "").split("?")[0]}`.toLowerCase();
            if (/image\/|\.(png|jpe?g|gif|webp)\b/.test(value)) return "image";
            if (/application\/pdf|\.pdf\b/.test(value)) return "pdf";
            return null;
        },
        formatSize(bytes) {
            if (!bytes && bytes !== 0) return "";
            if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
            return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
        },
        pick() {
            this.$refs.input.click();
        },
        onInput(event) {
            const [file] = event.target.files || [];
            event.target.value = "";
            if (file) this.choose(file);
        },
        onDrop(event) {
            this.dragging = false;
            const [file] = event.dataTransfer.files || [];
            if (file) this.choose(file);
        },
        choose(file) {
            if (file.size > this.maxSizeMb * 1024 * 1024) {
                this.$emit("error", `O arquivo tem ${this.formatSize(file.size)}; o limite é ${this.maxSizeMb} MB.`);
                return;
            }
            if (this.accept && !this.acceptsFile(file)) {
                this.$emit("error", `Formato não aceito. Use: ${this.hint}.`);
                return;
            }
            this.$emit("select", file);
        },
        acceptsFile(file) {
            const rules = this.accept.split(",").map((rule) => rule.trim().toLowerCase());
            const type = (file.type || "").toLowerCase();
            const name = file.name.toLowerCase();
            return rules.some((rule) => (rule.startsWith(".") ? name.endsWith(rule) : rule.endsWith("/*") ? type.startsWith(rule.slice(0, -1)) : type === rule));
        },
    },
};
</script>
